import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import MastersManagement from './components/MastersManagement';
import UserManagement from './components/UserManagement';
import ClientCodeSettings from './components/ClientCodeSettings';
import EmployeeCodeSettings from './components/EmployeeCodeSettings';
import ChangePasswordModal from './components/ChangePasswordModal';

const Settings = () => {
  const { userProfile } = useAuth();
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('masters');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const tabs = [
    { id: 'masters', label: 'Masters', icon: 'Database' },
    { id: 'users', label: 'User Management', icon: 'Users' },
    { id: 'client-codes', label: 'Client Codes', icon: 'Hash' },
    { id: 'employee-codes', label: 'Employee Codes', icon: 'UserCheck' },
    { id: 'account', label: 'My Account', icon: 'User' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(true)}
        notifications={[]}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Settings" size={20} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Settings</h1>
                <p className="text-muted-foreground mt-1">
                  Manage system masters and user settings
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-border">
            <nav className="flex space-x-8">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                >
                  <Icon name={tab.icon} size={16} />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="bg-card border border-border rounded-lg">
            {activeTab === 'masters' && <MastersManagement />}
            {activeTab === 'users' && <UserManagement />}
            {activeTab === 'client-codes' && <ClientCodeSettings />}
            {activeTab === 'employee-codes' && <EmployeeCodeSettings />}
            {activeTab === 'account' && (
              <div className="p-6">
                <div className="max-w-md">
                   <h3 className="text-lg font-semibold mb-4">Account Security</h3>
                   <div className="p-4 bg-muted rounded-lg border border-border mb-6">
                      <div className="flex items-center gap-4 mb-4">
                         <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center text-white text-xl font-bold">
                            {userProfile?.full_name?.charAt(0) || 'U'}
                         </div>
                         <div>
                            <p className="font-bold text-foreground">{userProfile?.full_name}</p>
                            <p className="text-sm text-muted-foreground">{userProfile?.email}</p>
                         </div>
                      </div>
                      <div className="space-y-2">
                         <p className="text-sm"><span className="text-muted-foreground">Role:</span> <span className="font-medium capitalize">{userProfile?.role}</span></p>
                         <p className="text-sm"><span className="text-muted-foreground">Department:</span> <span className="font-medium">{userProfile?.department || 'N/A'}</span></p>
                      </div>
                   </div>
                   
                   <div className="space-y-4">
                      <div>
                         <h4 className="font-medium mb-1">Password</h4>
                         <p className="text-sm text-muted-foreground mb-3">Change your account password to keep your account secure.</p>
                         <Button 
                            variant="outline" 
                            onClick={() => setShowPasswordModal(true)}
                            className="flex items-center gap-2"
                         >
                            <Icon name="Lock" size={16} />
                            Change Password
                         </Button>
                      </div>
                   </div>
                </div>
              </div>
            )}
          </div>
          
          <ChangePasswordModal 
            isOpen={showPasswordModal} 
            onClose={() => setShowPasswordModal(false)} 
          />
        </div>
      </main>
    </div>
  );
};

export default Settings;

